import React, { useState, useEffect } from 'react';
import { useApp } from '../../../contexts/AppContext';
import './FilterDrawer.css';

interface FilterDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({ isOpen, onClose }) => {
    const { products, productListSidebar, setProductListSidebar } = useApp();
    const { selectedCategory, priceRange, selectedTag } = productListSidebar;

    const [tempPriceRange, setTempPriceRange] = useState(priceRange);
    const [activeCategory, setActiveCategory] = useState(selectedCategory);
    const [activeTag, setActiveTag] = useState(selectedTag);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setTempPriceRange(priceRange);
            setActiveCategory(selectedCategory);
            setActiveTag(selectedTag);
            setIsCategoryDropdownOpen(false);
            // Disable scroll when drawer is open
            document.documentElement.classList.add('drawer-open');
            document.body.classList.add('drawer-open');
        } else {
            // Re-enable scroll when closed
            document.documentElement.classList.remove('drawer-open');
            document.body.classList.remove('drawer-open');
        }
    }, [isOpen, priceRange, selectedCategory, selectedTag]);

    useEffect(() => {
        return () => {
            // Cleanup on unmount
            document.documentElement.classList.remove('drawer-open');
            document.body.classList.remove('drawer-open');
        };
    }, []);

    const categoriesList = [
        { key: '', name: 'All Categories' },
        { key: 'tableDecor', name: 'Table Decor' },
        { key: 'bookMark', name: 'Book Mark' },
        { key: 'keyChain', name: 'Key Chain' },
        { key: 'photoFrame', name: 'Photo Frame' },
        { key: 'wallDecour', name: 'Wall Decour' },
        { key: 'penStand', name: 'Pen Stand' },
        { key: 'flowerPot', name: 'Flower Pot' },
        { key: 'corporateGifts', name: 'Corporate Gifts' }
    ];

    const getCategoryCount = (key: string) => {
        if (key === '') return products.length;
        return products.filter(p => p.category === key).length;
    };

    const allTags = Array.from(new Set(products.flatMap(p => p.tags || []))).sort();

    const handleClear = () => {
        setTempPriceRange({ min: 0, max: 1500 });
        setActiveCategory('');
        setActiveTag('');
    };

    const handleApply = () => {
        setProductListSidebar(prev => ({
            ...prev,
            selectedCategory: activeCategory,
            priceRange: tempPriceRange,
            selectedTag: activeTag
        }));
        onClose();
    };

    const isDirty =
        tempPriceRange.min !== priceRange.min ||
        tempPriceRange.max !== priceRange.max ||
        activeCategory !== selectedCategory ||
        activeTag !== selectedTag;

    const isDefault =
        tempPriceRange.min === 0 &&
        tempPriceRange.max === 1500 &&
        activeCategory === '' &&
        activeTag === '';

    const currentCategoryName = categoriesList.find(cat => cat.key === activeCategory)?.name || 'All Categories';

    // Slider logic
    const minValue = 0;
    const maxValue = 1500;

    const handleSliderClick = (e: React.MouseEvent) => {
        const slider = e.currentTarget;
        const rect = slider.getBoundingClientRect();
        const sliderWidth = rect.width;
        const startX = rect.left;
        const x = e.clientX - startX;
        const percentage = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
        const value = Math.round(minValue + (percentage / 100) * (maxValue - minValue));

        const minDistance = Math.abs(value - tempPriceRange.min);
        const maxDistance = Math.abs(value - tempPriceRange.max);

        if (minDistance < maxDistance) {
            setTempPriceRange(prev => ({ ...prev, min: Math.max(minValue, Math.min(value, prev.max)) }));
        } else {
            setTempPriceRange(prev => ({ ...prev, max: Math.min(maxValue, Math.max(value, prev.min)) }));
        }
    };

    const handleSliderDrag = (e: React.MouseEvent, type: 'min' | 'max') => {
        e.preventDefault();
        e.stopPropagation();
        const slider = (e.currentTarget as HTMLElement).parentElement;
        if (!slider) return;

        const rect = slider.getBoundingClientRect();
        const sliderWidth = rect.width;
        const startX = rect.left;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const x = moveEvent.clientX - startX;
            const percentage = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
            const value = Math.round(minValue + (percentage / 100) * (maxValue - minValue));

            if (type === 'min') {
                setTempPriceRange(prev => ({ ...prev, min: Math.max(minValue, Math.min(value, prev.max)) }));
            } else {
                setTempPriceRange(prev => ({ ...prev, max: Math.min(maxValue, Math.max(value, prev.min)) }));
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <>
            <div className={`filter-drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}></div>
            <div className={`filter-drawer ${isOpen ? 'open' : ''}`}>
                <div className="filter-drawer-header">
                    <span className="drawer-title">Product Filters</span>
                    <div className="close-drawer-btn" onClick={onClose}>
                        <svg className="arrow-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                        <svg className="x-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </div>
                </div>

                <div className="filter-drawer-content">
                    {/* Categories Section */}
                    <div className="filter-section">
                        <h4 className="filter-section-title">Categories</h4>
                        <div className="filter-dropdown-container">
                            <div
                                className={`filter-dropdown-trigger ${isCategoryDropdownOpen ? 'open' : ''}`}
                                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                            >
                                <span>{currentCategoryName}</span>
                                <div className={`chevron-icon ${isCategoryDropdownOpen ? 'up' : ''}`}></div>
                            </div>

                            {isCategoryDropdownOpen && (
                                <div className="filter-dropdown-menu">
                                    {categoriesList.map(cat => (
                                        <div
                                            key={cat.key}
                                            className={`filter-dropdown-item ${activeCategory === cat.key ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveCategory(cat.key);
                                                setIsCategoryDropdownOpen(false);
                                            }}
                                        >
                                            <span className="cat-name">{cat.name}</span>
                                            <span className="cat-count">({getCategoryCount(cat.key)})</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Price Filter Section */}
                    <div className="filter-section">
                        <h4 className="filter-section-title">Price Range</h4>
                        <div className="price_slider_wrapper">
                            <div
                                className="price_slider ui-slider ui-corner-all ui-slider-horizontal ui-widget ui-widget-content"
                                onClick={handleSliderClick}
                                style={{ height: '3px', background: '#f0f0f0', position: 'relative', cursor: 'pointer', margin: '30px 0 20px 0' }}
                            >
                                <div
                                    className="ui-slider-range ui-corner-all ui-widget-header"
                                    style={{
                                        position: 'absolute',
                                        height: '100%',
                                        background: '#EE5536',
                                        left: `${((tempPriceRange.min - minValue) / (maxValue - minValue)) * 100}%`,
                                        width: `${((tempPriceRange.max - tempPriceRange.min) / (maxValue - minValue)) * 100}%`
                                    }}
                                ></div>
                                <span
                                    className="ui-slider-handle ui-corner-all ui-state-default"
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '14px',
                                        height: '14px',
                                        background: '#fff',
                                        border: '2px solid #1c212a',
                                        borderRadius: '50%',
                                        left: `${((tempPriceRange.min - minValue) / (maxValue - minValue)) * 100}%`
                                    }}
                                    onMouseDown={(e) => handleSliderDrag(e, 'min')}
                                ></span>
                                <span
                                    className="ui-slider-handle ui-corner-all ui-state-default"
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '14px',
                                        height: '14px',
                                        background: '#fff',
                                        border: '2px solid #1c212a',
                                        borderRadius: '50%',
                                        left: `${((tempPriceRange.max - minValue) / (maxValue - minValue)) * 100}%`
                                    }}
                                    onMouseDown={(e) => handleSliderDrag(e, 'max')}
                                ></span>
                            </div>
                            <div className="price-slider-label">
                                Price: <span className="price-value">₹{tempPriceRange.min} — ₹{tempPriceRange.max}</span>
                            </div>
                        </div>
                    </div>

                    {/* Tags Section */}
                    <div className="filter-section">
                        <h4 className="filter-section-title">Tags</h4>
                        <div className="filter-tags-cloud">
                            <div
                                className={`filter-tag-item ${activeTag === '' ? 'active' : ''}`}
                                onClick={() => setActiveTag('')}
                            >
                                All Tags
                            </div>
                            {allTags.map(tag => (
                                <div
                                    key={tag}
                                    className={`filter-tag-item ${activeTag === tag ? 'active' : ''}`}
                                    onClick={() => setActiveTag(tag)}
                                >
                                    {tag}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="filter-drawer-footer">
                    <button
                        className={`clear-filters-btn ${isDefault ? 'disabled' : ''}`}
                        onClick={handleClear}
                        disabled={isDefault}
                    >
                        Clear
                    </button>
                    <button
                        className={`apply-filters-btn ${!isDirty ? 'disabled' : ''}`}
                        onClick={handleApply}
                        disabled={!isDirty}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </>
    );
};

export default FilterDrawer;
